import mustache from "mustache";
import {
  fx_createMonitoreoUsuarioRequest,
  MonitoreoUsuarioRequest,
} from "../../../../../modulos/configuracion/monitoreo/request/MonitoreoUsuarioRequest";
import { MonitoreoUsuarioResponse } from "../../../../../modulos/configuracion/monitoreo/response/MonitoreoUsuarioResponse";
import { monitoreoUsuarioSaveGrpcService } from "../../../../../modulos/configuracion/monitoreo/services/MonitoreoUsuarioSaveGrpcService";
import { BaseCreateRender } from "../../../../base/BaseCreateRender";
import { IFormField } from "../../../../definitions/form/IFormField";
import { FormCardRender } from "../../../../implementations/card/FormCardRender";
import { TextField } from "../../../../implementations/form/text/TextField";
import template from "./monitorAltaModalTemplate.html";

mustache.parse(template);

/** Modal de alta de un monitor de campo (auto-montado sobre `body`). Hospeda un
 *  `FormCardRender` con nombre y correo; el PIN lo genera el server y se lo
 *  envía por mail, así que no se pide acá.
 *
 *  La validación no se escribe a mano: `TextField` ya cubre requerido y formato
 *  de email (`type: "email"`), y `FormCardRender` se encarga del ciclo de
 *  submit (validar, enfocar el primer error, deshabilitar, guardar, reportar). */
export class MonitorAltaModal extends BaseCreateRender {
  /** Id del elemento raíz auto-montado en `body`. */
  protected readonly elementId = "monitorAltaModal";

  /** Card de formulario que da de alta al monitor. */
  private readonly _formCard: FormCardRender<
    MonitoreoUsuarioRequest,
    MonitoreoUsuarioResponse
  >;

  /**
   * @param clienteId  Cliente al que se le agrega el monitor.
   * @param _onCreated Notifica el monitor recién creado (con su PIN).
   */
  constructor(
    clienteId: string,
    private readonly _onCreated: (monitor: MonitoreoUsuarioResponse) => void,
  ) {
    super();
    const request = fx_createMonitoreoUsuarioRequest(clienteId, "", "");
    this._formCard = new FormCardRender<
      MonitoreoUsuarioRequest,
      MonitoreoUsuarioResponse
    >(request, {
      cardHeader: {
        title: "Agregar monitor",
        subtitle: "Se le enviará el PIN y las instrucciones a su correo.",
      },
      additionalCardBodyCssClass: "sx-card-body--gap",
      createFormFields: (req) => this._createFields(req),
      saveService: monitoreoUsuarioSaveGrpcService,
      submitText: "Enviar acceso",
      cancelAction: () => this.dispose(),
      onSubmitEnd: (monitor) => this._onSaveEnd(monitor),
    });
  }

  /** HTML del modal: overlay + contenido con el card del formulario. */
  protected getHtml(): string {
    return mustache.render(template, { card: this._formCard.render() });
  }

  /** Conecta el card, cablea el cierre por backdrop y anima la apertura. */
  protected afterInit(element: HTMLDivElement): void {
    this._formCard.connect();
    this._overlay = element.querySelector<HTMLDivElement>(".sx-modal-overlay");
    this._overlay?.addEventListener("click", this._onOverlayClick);
    requestAnimationFrame(() =>
      this._overlay?.classList.add("sx-modal-overlay--open"),
    );
  }

  /** Libera el listener del backdrop y el card antes de desmontar. */
  protected beforeDispose(_element: HTMLDivElement): void {
    this._overlay?.removeEventListener("click", this._onOverlayClick);
    this._formCard.dispose();
  }

  /** Elemento overlay (backdrop) del modal (disponible tras `afterInit`). */
  private _overlay: HTMLDivElement | null = null;

  /** Cierra el modal solo si el click fue en el backdrop, no en el contenido. */
  private _onOverlayClick = (e: MouseEvent): void => {
    if (e.target === this._overlay) this.dispose();
  };

  /** Tras el alta: avisa al listado y cierra. */
  private _onSaveEnd(monitor: MonitoreoUsuarioResponse): void {
    this._onCreated(monitor);
    this.dispose();
  }

  /** Campos del form: nombre + correo. */
  private _createFields(request: MonitoreoUsuarioRequest): IFormField[] {
    return [
      new TextField<MonitoreoUsuarioRequest>(
        request,
        {
          label: "Nombre",
          description: "Nombre con el que se identifica al monitor.",
          placeholder: "Nombre del monitor",
          type: "text",
          get: (r) => r.nombre,
          set: (r, value) => {
            r.nombre = value;
          },
        },
        { isRequired: true, maxLength: 100 },
      ),
      new TextField<MonitoreoUsuarioRequest>(
        request,
        {
          label: "Correo electrónico",
          description: "A esta dirección se envían el PIN y las instrucciones.",
          placeholder: "monitor@ejemplo.com",
          type: "email",
          get: (r) => r.email,
          set: (r, value) => {
            r.email = value;
          },
        },
        { isRequired: true, maxLength: 150 },
      ),
    ];
  }
}
